// Admin > Showcase (ticket MS-UI; spec-v4-models.md section 2, V4-MS): run the model showcase job (every video model
// on the same 12 style prompts) from a phone. Status (refreshed every 15 s while it runs), the dry-run plan, a cap,
// Start with a second-tap confirm showing the estimate (it spends real provider money, ours) and Stop. Admins only.
// GET /api/admin/model-showcase, POST /api/admin/model-showcase/{plan,start,stop}; the made clips show on /models.
import { useEffect, useRef, useState } from "react";
import { call } from "../api";
import { Empty, ErrorNote, Icon, Spinner, timeAgo, useLoad } from "../components/bits";
import { Shell } from "../components/Shell";
import { Link } from "../router";
import {
  CONFIRM_MS, DEFAULT_CAP_USD, DEFAULT_IMAGE_CAP_USD, imagePlanView, imageStartLabel, imageStatusFacts, parseImagePlan, parseImageStatus, parsePlan, parseStatus, planView, readCap, REFRESH_MS, startLabel, statusFacts,
  type AdminImagePlan, type AdminImageStatus, type AdminShowcasePlan, type AdminShowcaseStatus,
} from "../showcaseAdmin";
import { toast, useStore } from "../store";

// the mock app (VITE_MOCK=1) runs the job in memory (mock/showcaseAdmin.ts); the import is dropped from the real build
const MOCK = import.meta.env.VITE_MOCK === "1";
const mock = () => import("../mock/showcaseAdmin");
const getStatus = () => (MOCK ? mock().then((m) => m.mockShowcaseStatus()) : call("GET", "admin/model-showcase"));
const postPlan = (b: { capUsd: number }) => (MOCK ? mock().then((m) => m.mockShowcasePlan(b)) : call("POST", "admin/model-showcase/plan", b));
const postStart = (b: { capUsd: number; newRound?: boolean }) => (MOCK ? mock().then((m) => m.mockShowcaseStart(b)) : call("POST", "admin/model-showcase/start", b));
const postStop = () => (MOCK ? mock().then((m) => m.mockShowcaseStop()) : call("POST", "admin/model-showcase/stop"));
// MS-IMG: the image round, its own job and cap
const getImages = () => (MOCK ? mock().then((m) => m.mockImageStatus()) : call("GET", "admin/model-showcase/images"));
const postImagePlan = (b: { capUsd: number }) => (MOCK ? mock().then((m) => m.mockImagePlan(b)) : call("POST", "admin/model-showcase/images/plan", b));
const postImageStart = (b: { capUsd: number; newRound?: boolean }) => (MOCK ? mock().then((m) => m.mockImageStart(b)) : call("POST", "admin/model-showcase/images/start", b));
const postImageStop = () => (MOCK ? mock().then((m) => m.mockImageStop()) : call("POST", "admin/model-showcase/images/stop"));

/** Where the made clips show: the Models screen's showcase (V4-MS). */
const GALLERY = "/models";

export function AdminShowcaseScreen() {
  const me = useStore((s) => s.me);
  const admin = me?.user.role === "admin";
  const { data, setData, error, loading, reload } = useLoad(() => (admin ? getStatus().then(parseStatus) : Promise.resolve(null)), [admin]);
  const [capText, setCapText] = useState(String(DEFAULT_CAP_USD));
  const [plan, setPlan] = useState<AdminShowcasePlan | null>(null);
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState<"plan" | "start" | "stop" | null>(null);
  const [newRound, setNewRound] = useState(false);
  const disarm = useRef<ReturnType<typeof setTimeout> | null>(null);
  const running = data?.state === "running";

  // refresh every 15 s while it runs
  useEffect(() => {
    if (!running) return;
    const t = setInterval(reload, REFRESH_MS);
    return () => clearInterval(t);
  }, [running, reload]);
  useEffect(() => () => void (disarm.current && clearTimeout(disarm.current)), []);

  if (me && !admin)
    return (
      <Shell>
        <div className="page">
          <Empty title="Admins only" action={<Link to="/" className="btn btn-primary">Back home</Link>}>
            The model showcase is run by the studio's admins.
          </Empty>
        </div>
      </Shell>
    );

  const cap = readCap(capText);
  const capErr = "error" in cap ? cap.error : null;
  const unarm = () => {
    setArmed(false);
    if (disarm.current) clearTimeout(disarm.current);
  };

  const dryRun = async (): Promise<AdminShowcasePlan | null> => {
    if (!("usd" in cap)) return null;
    setBusy("plan");
    try {
      const p = parsePlan(await postPlan({ capUsd: cap.usd }));
      setPlan(p);
      return p;
    } catch (e) {
      toast(`Couldn't plan it. ${(e as Error).message}`, "bad");
      return null;
    } finally {
      setBusy(null);
    }
  };

  const start = async () => {
    if (!("usd" in cap)) return;
    if (!armed) {
      // first tap: a fresh estimate for this cap, then wait for the second
      const p = await dryRun();
      if (!p) return;
      setArmed(true);
      if (disarm.current) clearTimeout(disarm.current);
      disarm.current = setTimeout(() => setArmed(false), CONFIRM_MS);
      return;
    }
    unarm();
    setBusy("start");
    try {
      const s = parseStatus(await postStart({ capUsd: cap.usd, ...(newRound ? { newRound: true } : {}) }));
      setData(s);
      setPlan(null);
      setNewRound(false);
      toast("Started. It makes one piece at a time.", "good");
    } catch (e) {
      toast(`Couldn't start it. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(null);
    }
  };

  const stop = async () => {
    setBusy("stop");
    try {
      setData(parseStatus(await postStop()));
      toast("Stopping after the piece being made now.", "good");
    } catch (e) {
      toast(`Couldn't stop it. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Shell>
      <div className="page msa">
        <header className="page-head">
          <h1 className="page-title">Model showcase</h1>
          <p className="page-lede">Every video model, and every picture model, on the same 12 prompts. Each runs on our provider accounts, never a user's, and stops at its own cap.</p>
        </header>
        {error && <ErrorNote text={`Couldn't load the showcase job. ${error}`} retry={reload} />}
        {loading && !data && <Spinner label="Loading the showcase job" />}
        {data && (
          <>
            <StatusCard s={data} />

            <section className="msa-sec" aria-labelledby="msa-run-h">
              <h2 id="msa-run-h" className="group-title">
                {running ? "It's running" : "Run it"}
              </h2>
              {running ? (
                <div className="msa-acts">
                  <button type="button" className="btn btn-danger" data-action="showcase.stop" disabled={busy === "stop"} onClick={() => void stop()}>
                    <Icon name="stop" size={16} /> {busy === "stop" ? "Stopping" : "Stop"}
                  </button>
                  <p className="muted msa-note">Stop lets the piece being made now finish (it's already paid for).</p>
                </div>
              ) : (
                <>
                  <label className="field msa-cap">
                    <span className="field-label">Cap (USD)</span>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      inputMode="decimal"
                      value={capText}
                      onChange={(e) => {
                        setCapText(e.target.value);
                        unarm();
                      }}
                      aria-invalid={!!capErr}
                    />
                    <span className={capErr ? "field-error" : "field-hint"}>{capErr ?? "The run stops before any piece that would pass it. This round's spend counts."}</span>
                  </label>
                  {data.plan.spentUsd > 0 && (
                    <label className="check">
                      <input
                        type="checkbox"
                        checked={newRound}
                        onChange={(e) => {
                          setNewRound(e.target.checked);
                          unarm();
                        }}
                      />
                      <span>Fresh budget: start round {data.round + 1} (made clips are kept)</span>
                    </label>
                  )}
                  <div className="msa-acts">
                    <button type="button" className="btn btn-quiet" data-action="showcase.plan" disabled={!!busy || !!capErr} onClick={() => void dryRun()}>
                      {busy === "plan" && !armed ? "Planning" : "Plan"}
                    </button>
                    <button type="button" className={`btn ${armed ? "btn-danger" : "btn-primary"}`} data-action="showcase.start" disabled={!!busy || !!capErr} onClick={() => void start()} aria-live="polite">
                      {busy === "start" ? "Starting" : startLabel(plan, armed)}
                    </button>
                  </div>
                  {armed && <p className="msa-confirm" role="status">This spends real money. Tap again to start.</p>}
                </>
              )}
            </section>

            <PlanTable p={plan ?? data.plan} dry={!!plan} />

            <p className="msa-gallery">
              <Link to={GALLERY} className="btn btn-quiet btn-small">
                <Icon name="film" size={16} /> See the public showcase
              </Link>
            </p>
          </>
        )}
        {admin && <ImagesSection />}
      </div>
    </Shell>
  );
}

function StatusCard({ s }: { s: AdminShowcaseStatus }) {
  const f = statusFacts(s);
  return (
    <section className={`msa-card msa-${s.state}`} aria-labelledby="msa-status-h">
      <header className="msa-card-head">
        <h2 id="msa-status-h" className="msa-state">
          <span className="msa-dot" aria-hidden="true" />
          {f.state}
        </h2>
        <span className="muted">
          {f.round}
          {s.updatedAt ? ` · updated ${timeAgo(s.updatedAt)}` : ""}
        </span>
      </header>
      <dl className="msa-facts">
        <div>
          <dt>Clips</dt>
          <dd>{f.clips}</dd>
        </div>
        <div>
          <dt>Start frames</dt>
          <dd>{f.frames}</dd>
        </div>
        <div className="msa-spent">
          <dt>Spent</dt>
          <dd>{f.spent}</dd>
          <span className="msa-bar" aria-hidden="true">
            <span style={{ width: `${Math.round(f.spentShare * 100)}%` }} />
          </span>
        </div>
      </dl>
      {f.now && (
        <p className="msa-now">
          <span className="spinner-dot" aria-hidden /> Making {f.now}
        </p>
      )}
      {f.message && <p className="muted">{f.message}</p>}
      {f.narrowed && <p className="muted">The last run was narrowed to some styles or models (from the console). A start here covers everything.</p>}
      {f.failures.length > 0 && (
        <details className="msa-fails">
          <summary>
            {f.failures.length} recent {f.failures.length === 1 ? "failure" : "failures"}
          </summary>
          <ul>
            {f.failures.map((x, i) => (
              <li key={i}>
                <strong>{x.what}</strong> <span className="muted">({x.tries})</span>
                <span className="msa-err">{x.error}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function PlanTable({ p, dry }: { p: AdminShowcasePlan; dry: boolean }) {
  const v = planView(p);
  return (
    <section className="msa-sec" aria-labelledby="msa-plan-h">
      <h2 id="msa-plan-h" className="group-title">
        Plan
      </h2>
      {dry && <p className="tag msa-dry">Dry run with your cap: nothing queued</p>}
      <p className="msa-plan-title">{v.title}</p>
      <p className="muted msa-line">{v.progress}</p>
      <p className="muted msa-line">{v.frames}</p>
      {v.rows.length > 0 ? (
        <div className="table-wrap">
          <table className="costs-table msa-table">
            <thead>
              <tr>
                <th scope="col">Model</th>
                <th scope="col" className="num">
                  Clip
                </th>
                <th scope="col" className="num">
                  Length
                </th>
                <th scope="col" className="num">
                  To make
                </th>
                <th scope="col" className="num">
                  USD
                </th>
              </tr>
            </thead>
            <tbody>
              {v.rows.map((r) => (
                <tr key={r.id}>
                  <th scope="row">
                    {r.label}
                    <span className="msa-id">{r.id}</span>
                  </th>
                  <td className="num">{r.clip}</td>
                  <td className="num">{r.len}</td>
                  <td className="num">{r.toMake}</td>
                  <td className="num">{r.usd}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted">{v.nothingLeft ? "Every clip is made (or given up on). Nothing left to spend." : "No clips left to make."}</p>
      )}
      <dl className="msa-totals">
        <div>
          <dt>Estimate to finish</dt>
          <dd>{v.estimate}</dd>
        </div>
        <div>
          <dt>Spent this round</dt>
          <dd>{v.spent}</dd>
        </div>
      </dl>
      <p className={`msa-fits ${v.fits ? "is-ok" : "is-over"}`}>{v.fitsLine}</p>
    </section>
  );
}

// ------------------------------------------------------------------ MS-IMG: the picture models' round

/** The image showcase job: its own status, cap ($40 by default), plan, two-tap Start and Stop. */
function ImagesSection() {
  const { data, setData, error, loading, reload } = useLoad(() => getImages().then(parseImageStatus), []);
  const [capText, setCapText] = useState(String(DEFAULT_IMAGE_CAP_USD));
  const [plan, setPlan] = useState<AdminImagePlan | null>(null);
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState<"plan" | "start" | "stop" | null>(null);
  const [newRound, setNewRound] = useState(false);
  const disarm = useRef<ReturnType<typeof setTimeout> | null>(null);
  const running = data?.state === "running";

  useEffect(() => {
    if (!running) return;
    const t = setInterval(reload, REFRESH_MS);
    return () => clearInterval(t);
  }, [running, reload]);
  useEffect(() => () => void (disarm.current && clearTimeout(disarm.current)), []);

  const cap = readCap(capText);
  const capErr = "error" in cap ? cap.error : null;
  const unarm = () => {
    setArmed(false);
    if (disarm.current) clearTimeout(disarm.current);
  };
  const dryRun = async (): Promise<AdminImagePlan | null> => {
    if (!("usd" in cap)) return null;
    setBusy("plan");
    try {
      const p = parseImagePlan(await postImagePlan({ capUsd: cap.usd }));
      setPlan(p);
      return p;
    } catch (e) {
      toast(`Couldn't plan the pictures. ${(e as Error).message}`, "bad");
      return null;
    } finally {
      setBusy(null);
    }
  };
  const start = async () => {
    if (!("usd" in cap)) return;
    if (!armed) {
      const p = await dryRun();
      if (!p) return;
      setArmed(true);
      if (disarm.current) clearTimeout(disarm.current);
      disarm.current = setTimeout(() => setArmed(false), CONFIRM_MS);
      return;
    }
    unarm();
    setBusy("start");
    try {
      setData(parseImageStatus(await postImageStart({ capUsd: cap.usd, ...(newRound ? { newRound: true } : {}) })));
      setPlan(null);
      setNewRound(false);
      toast("Started. It draws one picture at a time.", "good");
    } catch (e) {
      toast(`Couldn't start it. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(null);
    }
  };
  const stop = async () => {
    setBusy("stop");
    try {
      setData(parseImageStatus(await postImageStop()));
      toast("Stopping after the picture being drawn now.", "good");
    } catch (e) {
      toast(`Couldn't stop it. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="msa-images" aria-labelledby="msa-img-h">
      <header className="msa-images-head">
        <h2 id="msa-img-h" className="page-title msa-images-title">
          Picture models
        </h2>
        <p className="page-lede">Every picture model draws the same 12 prompts, plus two rows from one reference picture. About $10 in all, with its own cap.</p>
      </header>
      {error && <ErrorNote text={`Couldn't load the picture job. ${error}`} retry={reload} />}
      {loading && !data && <Spinner label="Loading the picture job" />}
      {data && (
        <>
          <ImageStatusCard s={data} />
          <section className="msa-sec" aria-labelledby="msa-img-run-h">
            <h3 id="msa-img-run-h" className="group-title">
              {running ? "It's running" : "Run it"}
            </h3>
            {running ? (
              <div className="msa-acts">
                <button type="button" className="btn btn-danger" data-action="showcase.images-stop" disabled={busy === "stop"} onClick={() => void stop()}>
                  <Icon name="stop" size={16} /> {busy === "stop" ? "Stopping" : "Stop"}
                </button>
                <p className="muted msa-note">The picture being drawn now finishes (it's already paid for).</p>
              </div>
            ) : (
              <>
                <label className="field msa-cap">
                  <span className="field-label">Cap (USD)</span>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    inputMode="decimal"
                    value={capText}
                    onChange={(e) => {
                      setCapText(e.target.value);
                      unarm();
                    }}
                    aria-invalid={!!capErr}
                  />
                  <span className={capErr ? "field-error" : "field-hint"}>{capErr ?? "Separate from the video cap. It stops before any picture that would pass it."}</span>
                </label>
                {data.plan.spentUsd > 0 && (
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={newRound}
                      onChange={(e) => {
                        setNewRound(e.target.checked);
                        unarm();
                      }}
                    />
                    <span>Fresh budget: start round {data.round + 1} (made pictures are kept)</span>
                  </label>
                )}
                <div className="msa-acts">
                  <button type="button" className="btn btn-quiet" data-action="showcase.images-plan" disabled={!!busy || !!capErr} onClick={() => void dryRun()}>
                    {busy === "plan" && !armed ? "Planning" : "Plan"}
                  </button>
                  <button type="button" className={`btn ${armed ? "btn-danger" : "btn-primary"}`} data-action="showcase.images-start" disabled={!!busy || !!capErr} onClick={() => void start()} aria-live="polite">
                    {busy === "start" ? "Starting" : imageStartLabel(plan, armed)}
                  </button>
                </div>
                {armed && <p className="msa-confirm" role="status">This spends real money. Tap again to start.</p>}
              </>
            )}
          </section>
          <ImagePlanTable p={plan ?? data.plan} dry={!!plan} />
          <p className="msa-gallery">
            <Link to="/models?tab=images" className="btn btn-quiet btn-small">
              <Icon name="image" size={16} /> See the picture showcase
            </Link>
          </p>
        </>
      )}
    </section>
  );
}

function ImageStatusCard({ s }: { s: AdminImageStatus }) {
  const f = imageStatusFacts(s);
  return (
    <section className={`msa-card msa-${s.state}`} aria-labelledby="msa-img-status-h">
      <header className="msa-card-head">
        <h3 id="msa-img-status-h" className="msa-state">
          <span className="msa-dot" aria-hidden="true" />
          {f.state}
        </h3>
        <span className="muted">
          {f.round}
          {s.updatedAt ? ` · updated ${timeAgo(s.updatedAt)}` : ""}
        </span>
      </header>
      <dl className="msa-facts">
        <div>
          <dt>Pictures</dt>
          <dd>{f.pictures}</dd>
        </div>
        <div className="msa-spent">
          <dt>Spent</dt>
          <dd>{f.spent}</dd>
          <span className="msa-bar" aria-hidden="true">
            <span style={{ width: `${Math.round(f.spentShare * 100)}%` }} />
          </span>
        </div>
      </dl>
      {f.now && (
        <p className="msa-now">
          <span className="spinner-dot" aria-hidden /> Drawing {f.now}
        </p>
      )}
      {f.message && <p className="muted">{f.message}</p>}
      {f.narrowed && <p className="muted">The last run was narrowed (from the console). A start here covers everything.</p>}
      {f.failures.length > 0 && (
        <details className="msa-fails">
          <summary>
            {f.failures.length} recent {f.failures.length === 1 ? "failure" : "failures"}
          </summary>
          <ul>
            {f.failures.map((x, i) => (
              <li key={i}>
                <strong>{x.what}</strong> <span className="muted">({x.tries})</span>
                <span className="msa-err">{x.error}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function ImagePlanTable({ p, dry }: { p: AdminImagePlan; dry: boolean }) {
  const v = imagePlanView(p);
  return (
    <section className="msa-sec" aria-labelledby="msa-img-plan-h">
      <h3 id="msa-img-plan-h" className="group-title">
        Plan
      </h3>
      {dry && <p className="tag msa-dry">Dry run with your cap: nothing queued</p>}
      <p className="msa-plan-title">{v.title}</p>
      <p className="muted msa-line">{v.progress}</p>
      {v.reused && <p className="muted msa-line">{v.reused}</p>}
      {v.rows.length > 0 && !v.nothingLeft ? (
        <div className="table-wrap">
          <table className="costs-table msa-table">
            <thead>
              <tr>
                <th scope="col">Model</th>
                <th scope="col" className="num">
                  Picture
                </th>
                <th scope="col" className="num">
                  With ref
                </th>
                <th scope="col" className="num" title="What a user pays per picture">
                  Credits
                </th>
                <th scope="col" className="num">
                  To make
                </th>
                <th scope="col" className="num">
                  USD
                </th>
              </tr>
            </thead>
            <tbody>
              {v.rows.map((r) => (
                <tr key={r.id}>
                  <th scope="row">
                    {r.label}
                    <span className="msa-id">{r.id}</span>
                  </th>
                  <td className="num">{r.image}</td>
                  <td className="num">{r.withRef}</td>
                  <td className="num">{r.creditsLabel}</td>
                  <td className="num">{r.toMake}</td>
                  <td className="num">{r.usd}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted">Every picture is made (or given up on). Nothing left to spend.</p>
      )}
      <dl className="msa-totals">
        <div>
          <dt>Estimate to finish</dt>
          <dd>{v.estimate}</dd>
        </div>
        <div>
          <dt>Spent this round</dt>
          <dd>{v.spent}</dd>
        </div>
      </dl>
      <p className={`msa-fits ${v.fits ? "is-ok" : "is-over"}`}>{v.fitsLine}</p>
    </section>
  );
}
